import { useState, useEffect, useRef, useCallback } from "react";

export const PULL_NEXT_STORAGE_KEY = "bible_pull_next_enabled";
export const PULL_NEXT_THRESHOLD = 90;

export const getPullNextChapterPref = () => {
  try {
    const val = localStorage.getItem(PULL_NEXT_STORAGE_KEY);
    return val === null ? true : val !== "false";
  } catch (e) {
    return true;
  }
};

export const setPullNextChapterPref = (enabled) => {
  try {
    localStorage.setItem(PULL_NEXT_STORAGE_KEY, enabled ? "true" : "false");
    window.dispatchEvent(new Event("bible_pull_next_changed"));
  } catch (e) {}
};

export const isWindowAtBottom = (tolerance = 14) => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }
  const scrollHeight = Math.max(
    document.documentElement?.scrollHeight || 0,
    document.body?.scrollHeight || 0
  );
  if (scrollHeight <= 0) return false;
  return window.innerHeight + window.scrollY >= scrollHeight - tolerance;
};

const usePullNextChapter = ({
  hasNext = false,
  nextChapter = null,
  onNext,
  isBlocked = false,
  threshold = PULL_NEXT_THRESHOLD,
}) => {
  const [prefEnabled, setPrefEnabled] = useState(getPullNextChapterPref);
  const [pullDistance, setPullDistanceState] = useState(0);
  const pullDistanceRef = useRef(0);
  const touchStartRef = useRef({ x: 0, y: 0, atBottom: false });
  const wheelAccumRef = useRef(0);
  const wheelTimerRef = useRef(null);
  const triggeredRef = useRef(false);

  const setPullDistance = useCallback((val) => {
    pullDistanceRef.current = val;
    setPullDistanceState(val);
  }, []);

  useEffect(() => {
    const sync = () => setPrefEnabled(getPullNextChapterPref());
    window.addEventListener("bible_pull_next_changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("bible_pull_next_changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  // Reset pull state on chapter change or block
  useEffect(() => {
    setPullDistance(0);
    wheelAccumRef.current = 0;
    triggeredRef.current = false;
  }, [nextChapter?.id, isBlocked, prefEnabled, setPullDistance]);

  // Idle preload next chapter content lines
  useEffect(() => {
    if (!prefEnabled || !nextChapter?.content) return undefined;
    const schedule =
      typeof window !== "undefined" && typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback
        : (cb) => setTimeout(cb, 200);
    const cancel =
      typeof window !== "undefined" && typeof window.cancelIdleCallback === "function"
        ? window.cancelIdleCallback
        : clearTimeout;

    const handle = schedule(() => {
      // Touch nextChapter.content in idle time so string split is hot in memory
      void nextChapter.content.length;
    });
    return () => cancel(handle);
  }, [prefEnabled, nextChapter]);

  const triggerAdvance = useCallback(() => {
    if (triggeredRef.current) return;
    if (!hasNext || typeof onNext !== "function") return;
    triggeredRef.current = true;
    setPullDistance(0);
    wheelAccumRef.current = 0;
    onNext();
    if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [hasNext, onNext, setPullDistance]);

  useEffect(() => {
    if (!prefEnabled || isBlocked) {
      setPullDistance(0);
      return undefined;
    }

    const handleWheel = (e) => {
      if (e.deltaY <= 0) {
        if (wheelAccumRef.current > 0) {
          wheelAccumRef.current = 0;
          setPullDistance(0);
        }
        return;
      }

      if (!isWindowAtBottom()) return;

      wheelAccumRef.current = Math.min(
        threshold * 1.35,
        wheelAccumRef.current + e.deltaY * 0.45
      );
      setPullDistance(wheelAccumRef.current);

      if (wheelTimerRef.current) {
        clearTimeout(wheelTimerRef.current);
      }
      wheelTimerRef.current = setTimeout(() => {
        if (wheelAccumRef.current >= threshold && hasNext) {
          triggerAdvance();
        } else {
          wheelAccumRef.current = 0;
          setPullDistance(0);
        }
      }, 260);
    };

    const handleTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        atBottom: isWindowAtBottom(),
      };
    };

    const handleTouchMove = (e) => {
      if (e.touches.length !== 1) return;
      if (!touchStartRef.current.atBottom && !isWindowAtBottom()) return;

      const dx = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
      const dy = touchStartRef.current.y - e.touches[0].clientY; // positive when pulling up

      if (dy <= 16 || dx > dy * 0.75) {
        if (pullDistanceRef.current > 0) setPullDistance(0);
        return;
      }

      const damped = Math.min(threshold * 1.35, (dy - 16) * 0.6);
      setPullDistance(damped);
    };

    const handleTouchEnd = () => {
      if (pullDistanceRef.current >= threshold && hasNext) {
        triggerAdvance();
      } else {
        setPullDistance(0);
      }
      touchStartRef.current.atBottom = false;
    };

    window.addEventListener("wheel", handleWheel, { passive: true });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current);
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [prefEnabled, isBlocked, threshold, hasNext, setPullDistance, triggerAdvance]);

  const pullProgress = Math.min(1, pullDistance / threshold);

  return {
    pullEnabled: prefEnabled,
    pullDistance,
    pullProgress,
    isPulling: pullDistance > 6,
    isReadyToAdvance: pullProgress >= 1 && hasNext,
  };
};

export default usePullNextChapter;
